import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { eventsApi, paymentApi } from '../utils/api';
import { IconCheckCircle } from '../components/Icons';

export default function CompletePayment() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [squareAppId, setSquareAppId] = useState(null);
  const [squareLocationId, setSquareLocationId] = useState(null);
  const [sandboxBypass, setSandboxBypass] = useState(false);
  const [cardReady, setCardReady] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [paid, setPaid] = useState(false);
  const cardInstanceRef = useRef(null);

  // Compute amount due from tier price, discount, and amount already paid
  const amountDue = event && event.tier
    ? (parseFloat(event.tier.price_cad) - parseFloat(event.discount_amount || 0) - parseFloat(event.amount_paid || 0)).toFixed(2)
    : null;
  const isFreeEvent = amountDue !== null && parseFloat(amountDue) <= 0;

  useEffect(() => {
    Promise.all([
      eventsApi.getEvent(eventId),
      paymentApi.getSquareAppId()
    ]).then(([eventRes, squareRes]) => {
      const ev = eventRes.data.data;
      if (ev.status !== 'pending_payment') {
        setError('This event does not require payment.');
      }
      setEvent(ev);
      setSquareAppId(squareRes.data.data.app_id);
      setSquareLocationId(squareRes.data.data.location_id);
      if (squareRes.data.data.sandbox_bypass) setSandboxBypass(true);
    }).catch(err => {
      setError(err.response?.data?.message || 'Failed to load event.');
    }).finally(() => setLoading(false));
  }, [eventId]);

  const initSquareCard = useCallback(async () => {
    if (!window.Square || !squareAppId || !squareLocationId) return;
    try {
      const payments = window.Square.payments(squareAppId, squareLocationId);
      const card = await payments.card();
      await card.attach('#card-container');
      cardInstanceRef.current = card;
      setCardReady(true);
    } catch (err) {
      console.error('Square card init error:', err);
      setError('Failed to load payment form. Please refresh and try again.');
    }
  }, [squareAppId, squareLocationId]);

  useEffect(() => {
    if (event && event.status === 'pending_payment' && squareAppId && squareLocationId && !paid && !isFreeEvent) {
      const timer = setTimeout(initSquareCard, 100);
      return () => {
        clearTimeout(timer);
        if (cardInstanceRef.current) {
          cardInstanceRef.current.destroy();
          cardInstanceRef.current = null;
          setCardReady(false);
        }
      };
    }
  }, [event, squareAppId, squareLocationId, paid, initSquareCard]);

  async function handleFreeActivate() {
    setError('');
    setPaymentProcessing(true);
    try {
      const { data } = await paymentApi.payForEvent(event.id, 'coupon-free');
      if (data.success) {
        setPaid(true);
      } else {
        setError(data.message || 'Activation failed.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Activation failed.');
    } finally {
      setPaymentProcessing(false);
    }
  }

  async function handleSandboxActivate() {
    setError('');
    setPaymentProcessing(true);
    try {
      const { data } = await paymentApi.payForEvent(event.id, 'sandbox-bypass');
      if (data.success) {
        setPaid(true);
      } else {
        setError(data.message || 'Activation failed.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Activation failed.');
    } finally {
      setPaymentProcessing(false);
    }
  }

  async function handlePayment() {
    setError('');
    if (!cardInstanceRef.current) {
      return setError('Payment form not ready. Please wait a moment and try again.');
    }
    setPaymentProcessing(true);
    try {
      const result = await cardInstanceRef.current.tokenize();
      if (result.status !== 'OK') {
        setPaymentProcessing(false);
        return setError(result.errors?.[0]?.message || 'Card verification failed. Please check your card details.');
      }
      const { data } = await paymentApi.payForEvent(event.id, result.token);
      if (data.success) {
        setPaid(true);
      } else {
        setError(data.message || 'Payment failed. Please try again.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Payment processing failed. Please try again.');
    } finally {
      setPaymentProcessing(false);
    }
  }

  if (loading) return <div className="spinner" />;

  return (
    <div className="page">
      <div className="page-content">
        <div className="page-header">
          <Link to="/dashboard" style={{ fontSize: '0.82rem', color: '#8a9bb5', display: 'inline-block', marginBottom: 12 }}>← Back to Dashboard</Link>
          <h1>Complete Payment</h1>
          <p style={{ color: '#8a9bb5' }}>Finish setting up your event by completing payment</p>
        </div>

        {error && !paid && <div className="alert alert-error">{error}</div>}

        {/* Payment form */}
        {event && event.status === 'pending_payment' && !paid && (
          <div className="card">
            <h3 style={{ marginBottom: 8 }}>Payment Required</h3>
            <p style={{ color: '#8a9bb5', fontSize: '0.85rem', marginBottom: 24 }}>
              Your event was created but payment was not completed. Complete payment to activate it.
            </p>
            <div style={{ background: '#1e2d3d', borderRadius: 10, padding: 20, marginBottom: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                <span style={{ color: '#8a9bb5' }}>Event</span>
                <span style={{ color: '#f0f2f5', fontWeight: 500 }}>{event.name}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                <span style={{ color: '#8a9bb5' }}>Format</span>
                <span style={{ color: '#f0f2f5' }}>{event.event_type === 'team' ? 'Team' : 'Individual'}</span>
              </div>
              {event.tier && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                  <span style={{ color: '#8a9bb5' }}>Tier</span>
                  <span style={{ color: '#f0f2f5' }}>{event.tier.tier_name} (up to {event.tier.max_attendees} attendees)</span>
                </div>
              )}
              {parseFloat(event.discount_amount) > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                  <span style={{ color: '#3adc8a' }}>Coupon Discount {event.coupon_code && <span style={{ fontWeight: 600 }}>({event.coupon_code})</span>}</span>
                  <span style={{ color: '#3adc8a', fontWeight: 600 }}>-${parseFloat(event.discount_amount).toFixed(2)}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 12, borderTop: '1px solid #2a3f56' }}>
                <span style={{ color: '#f0f2f5', fontWeight: 600 }}>Total Due</span>
                <span style={{ color: '#e8a838', fontFamily: 'Oswald, sans-serif', fontSize: '1.4rem', fontWeight: 700 }}>
                  ${amountDue} <span style={{ fontSize: '0.7rem', color: '#8a9bb5' }}>CAD</span>
                </span>
              </div>
            </div>
            {isFreeEvent ? (
              <div style={{ marginBottom: 20, padding: 16, background: 'rgba(58,220,138,0.08)', borderRadius: 8, border: '1px solid #3adc8a' }}>
                <p style={{ color: '#3adc8a', fontSize: '0.85rem', fontWeight: 600, marginBottom: 4 }}>No Payment Required</p>
                <p style={{ color: '#8a9bb5', fontSize: '0.78rem' }}>Your coupon covers the full amount. Click below to activate your event.</p>
              </div>
            ) : sandboxBypass ? (
              <div style={{ marginBottom: 20, padding: 16, background: '#1a2e1a', borderRadius: 8, border: '1px solid #2a5a2a' }}>
                <p style={{ color: '#3adc8a', fontSize: '0.85rem', fontWeight: 600, marginBottom: 4 }}>Sandbox Mode</p>
                <p style={{ color: '#8a9bb5', fontSize: '0.78rem' }}>Square credentials not configured. Payment will be bypassed and the event will be activated directly.</p>
              </div>
            ) : (
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', color: '#f0f2f5', fontWeight: 600, fontSize: '0.85rem', marginBottom: 10 }}>Card Details</label>
                <div id="card-container" style={{ minHeight: 50, background: '#111f2e', borderRadius: 8, padding: '12px', border: '1px solid #2a3f56' }}></div>
                {!cardReady && squareAppId && (
                  <p style={{ color: '#8a9bb5', fontSize: '0.78rem', marginTop: 8 }}>Loading payment form...</p>
                )}
                {!squareAppId && (
                  <p style={{ color: '#e85d5d', fontSize: '0.78rem', marginTop: 8 }}>Payment configuration not available. Please contact support.</p>
                )}
              </div>
            )}
            {isFreeEvent ? (
              <button className="btn btn-primary" style={{ width: '100%' }} onClick={handleFreeActivate} disabled={paymentProcessing}>
                {paymentProcessing ? 'Activating...' : 'Activate Event (Free)'}
              </button>
            ) : sandboxBypass ? (
              <button className="btn btn-primary" style={{ width: '100%' }} onClick={handleSandboxActivate} disabled={paymentProcessing}>
                {paymentProcessing ? 'Activating...' : `Activate Event (Sandbox)`}
              </button>
            ) : (
              <button className="btn btn-primary" style={{ width: '100%' }} onClick={handlePayment} disabled={paymentProcessing || !cardReady}>
                {paymentProcessing ? 'Processing Payment...' : `Pay $${amountDue} CAD`}
              </button>
            )}
          </div>
        )}

        {/* Success */}
        {paid && event && (
          <div className="card" style={{ textAlign: 'center', padding: '48px 32px' }}>
            <div style={{ marginBottom: 16, color: '#3adc8a' }}><IconCheckCircle size="56px" /></div>
            <h2 style={{ color: '#3adc8a', marginBottom: 8 }}>Payment Successful!</h2>
            <p style={{ color: '#8a9bb5', marginBottom: 24 }}>
              <strong style={{ color: '#f0f2f5' }}>{event.name}</strong> is now active.
              {event.event_type === 'team'
                ? ' Add tournaments, teams, and sponsors from the event page.'
                : ' Add tournaments, participants, and sponsors from the event page.'}
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to={`/events/${event.id}`} className="btn btn-primary">Manage Event →</Link>
              <Link to={`/events/${event.id}/scoreboard`} className="btn btn-secondary" target="_blank">Preview Scoreboard</Link>
            </div>
          </div>
        )}

        {/* Event doesn't need payment */}
        {event && event.status !== 'pending_payment' && !paid && (
          <div className="card" style={{ textAlign: 'center', padding: '40px 32px' }}>
            <p style={{ color: '#8a9bb5', marginBottom: 16 }}>This event does not require payment.</p>
            <Link to={`/events/${event.id}`} className="btn btn-primary">Go to Event →</Link>
          </div>
        )}
      </div>
    </div>
  );
}
